import { z } from 'zod';
import type { AccountConstraint } from '@/types/schemas';

export const CONSTRAINT_CODES = [
  'ma_bp',
  'ma_vv',
  'ma_hd',
  'ma_dtt',
  'ma_ku',
  'ma_phi',
  'ma_sp',
  'ma_lsx',
  'ma_cp0'
] as const;

export type AccountConstraintCode = (typeof CONSTRAINT_CODES)[number];

export const hasConstraintValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasConstraintValue);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasConstraintValue);
  }
  return true;
};

export const isConstraintCode = (constraintCode: string): constraintCode is AccountConstraintCode =>
  CONSTRAINT_CODES.includes(constraintCode as AccountConstraintCode);

export const constraintValue = (row: Record<string, unknown>, constraintCode: string) => {
  const rowValue = row[`${constraintCode}_data`] ?? row[constraintCode];

  if (!rowValue || typeof rowValue !== 'object') {
    return typeof rowValue === 'string' ? rowValue : '';
  }

  const record = rowValue as Record<string, unknown>;
  const value = [record.uuid, record.code, ...Object.values(record)].find(
    item => (typeof item === 'string' || typeof item === 'number') && hasConstraintValue(item)
  );

  return value === undefined ? '' : String(value);
};

export const constraintValues = (row: Record<string, unknown>, constraintCodes: readonly string[] = CONSTRAINT_CODES) =>
  constraintCodes.reduce<Record<string, string>>((values, constraintCode) => {
    values[constraintCode] = constraintValue(row, constraintCode);
    return values;
  }, {});

export const constraintKey = (constraints: AccountConstraint[] | null | undefined) =>
  (constraints ?? [])
    .map(
      constraint =>
        `${constraint.uuid ?? constraint.account ?? ''}:${constraint.constraint_code}:${constraint.is_mandatory ? 1 : 0}`
    )
    .join('|');

export const constraintSignature = constraintKey;

type ConstraintFieldSchema = z.ZodOptional<z.ZodNullable<z.ZodString>> | z.ZodOptional<z.ZodUnknown>;

export const constraintFields = CONSTRAINT_CODES.reduce<Record<string, ConstraintFieldSchema>>((fields, fieldName) => {
  fields[fieldName] = z.string().nullable().optional();
  fields[`${fieldName}_data`] = z.unknown().optional();
  return fields;
}, {});

interface ConstraintValidationOptions<TValues> {
  detailFieldName?: string;
  detailPath?: Array<string | number>;
  getRows?: (values: TValues) => unknown;
  constraintCodes?: readonly string[];
  message?: (constraint: AccountConstraint) => string;
}

const getValueAtPath = (value: unknown, path: Array<string | number>) =>
  path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string | number, unknown>)[key];
  }, value);

export const ConstraintSchema = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  constraints: AccountConstraint[] | null = [],
  {
    detailFieldName = 'chi_tiet',
    detailPath,
    getRows,
    constraintCodes = CONSTRAINT_CODES,
    message = constraint => `${constraint.constraint_name} không được để trống`
  }: ConstraintValidationOptions<z.infer<TSchema>> = {}
) => {
  const rowPath = detailPath ?? [detailFieldName];
  const constraintCodeSet = new Set(constraintCodes);

  return schema.superRefine((values, ctx) => {
    const mandatoryConstraints = (constraints ?? []).filter(
      constraint => constraint.is_mandatory && constraintCodeSet.has(constraint.constraint_code)
    );

    if (!mandatoryConstraints.length) {
      return;
    }

    const rows = getRows ? getRows(values) : getValueAtPath(values, rowPath);

    if (!Array.isArray(rows)) {
      return;
    }

    rows.forEach((row, rowIndex) => {
      if (!row || typeof row !== 'object') {
        return;
      }

      mandatoryConstraints.forEach(constraint => {
        const rowRecord = row as Record<string, unknown>;
        const rowValue = rowRecord[`${constraint.constraint_code}_data`] ?? rowRecord[constraint.constraint_code];

        if (hasConstraintValue(rowValue)) {
          return;
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...rowPath, rowIndex, constraint.constraint_code],
          message: message(constraint)
        });
      });
    });
  });
};
